import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
  Alert,
  Snackbar,
  Divider,
  Checkbox,
  FormGroup,
} from '@mui/material';
import ResponsiveDialog from './ResponsiveDialog';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Place as PlaceIcon,
  AutoFixHigh as AutoFixHighIcon,
} from '@mui/icons-material';
import apiService from '../services/api';
import {
  PROVIDER_PLACE_COLOR_PALETTE,
  defaultProviderPlaceColorForIndex,
  normalizeProviderPlaceColor,
  resolveProviderPlaceColor,
} from '../utils/providerPlaceColor';
import OpeningHoursEditor from './OpeningHoursEditor';
import {
  emptyOpeningHours,
  formatOpeningHoursSummary,
  hasConfiguredOpeningHours,
  normalizeOpeningHours,
  openingHoursSignature,
  readOpeningHoursFromPlace,
  validateOpeningHours,
  type OpeningHours,
} from '../utils/providerPlaceOpeningHours';

interface ProviderStorageBoxRow {
  id: number;
  name: string;
  description?: string | null;
  is_active: boolean;
  sort_order: number;
  provider_item_id?: number;
  category_id?: number | null;
  category?: { id: number; name: string; storage_box_prefix?: string | null };
  provider_place?: {
    id: number;
    name: string;
    display_name: string;
    color?: string | null;
    display_color?: string | null;
  };
}

export interface ProviderPlaceItem {
  id: number;
  name: string;
  description: string;
  attributes: Record<string, unknown>;
  formatted_attributes: Record<string, { label: string; value: unknown }>;
  display_name: string;
  is_active: boolean;
  sort_order: number;
  color?: string | null;
  display_color?: string | null;
  opening_hours?: OpeningHours;
}

interface ProviderPlacesSectionProps {
  providerId: number;
  /** Attributs optionnels du type (clé → config) pour le formulaire avancé */
  defaultAttributesTemplate?: Record<string, { type?: string; label?: string; options?: string[]; required?: boolean }>;
  placeStorageMode?: 'combined' | 'split' | 'central';
}

const ProviderPlacesSection: React.FC<ProviderPlacesSectionProps> = ({
  providerId,
  defaultAttributesTemplate = {},
  placeStorageMode: placeStorageModeProp,
}) => {
  const [items, setItems] = useState<ProviderPlaceItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState<ProviderPlaceItem | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    attributes: {} as Record<string, unknown>,
    sort_order: 0,
    is_active: true,
    color: defaultProviderPlaceColorForIndex(0),
    opening_hours: emptyOpeningHours(),
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({ open: false, message: '', severity: 'info' });

  const [storageBoxes, setStorageBoxes] = useState<ProviderStorageBoxRow[]>([]);
  const [resolvedPlaceStorageMode, setResolvedPlaceStorageMode] = useState<'combined' | 'split' | 'central'>(
    placeStorageModeProp === 'split' || placeStorageModeProp === 'central'
      ? placeStorageModeProp
      : 'combined'
  );
  const effectiveMode = placeStorageModeProp ?? resolvedPlaceStorageMode;
  const isSplitMode = effectiveMode === 'split';
  const isCentralMode = effectiveMode === 'central';
  const usesCentralBoxes = isSplitMode || isCentralMode;
  const [boxDialogOpen, setBoxDialogOpen] = useState(false);
  const [editingBox, setEditingBox] = useState<ProviderStorageBoxRow | null>(null);
  const [boxForm, setBoxForm] = useState({
    provider_item_id: '' as number | '',
    category_id: '' as number | '',
    name: '',
    description: '',
    sort_order: 0,
    is_active: true,
  });
  const [categoriesList, setCategoriesList] = useState<
    Array<{ id: number; name: string; storage_box_prefix?: string | null; is_active?: boolean }>
  >([]);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkForm, setBulkForm] = useState({
    placeIds: [] as number[],
    categoryIds: [] as number[],
    countPerCategory: 1,
  });

  const creatableCategories = useMemo(
    () =>
      categoriesList.filter(
        (c) =>
          c.is_active !== false &&
          c.storage_box_prefix &&
          String(c.storage_box_prefix).replace(/[^A-Za-z]/g, '').length >= 3
      ),
    [categoriesList]
  );

  const formatCategoryBoxPrefix = (c: { name: string; storage_box_prefix?: string | null }) => {
    const stored = String(c.storage_box_prefix || '').replace(/[^A-Za-z]/g, '');
    if (stored.length >= 3) {
      return stored.slice(0, 3).toUpperCase();
    }
    const fromName = c.name.replace(/[^A-Za-z]/g, '');
    if (fromName.length > 0) {
      return fromName.slice(0, 3).toUpperCase().padEnd(3, 'X');
    }
    return '???';
  };

  const categoryHasStoredPrefix = (c: { storage_box_prefix?: string | null }) =>
    String(c.storage_box_prefix || '').replace(/[^A-Za-z]/g, '').length >= 3;

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchStorageBoxes = useCallback(async () => {
    try {
      const response = await apiService.get(`/provider-storage-boxes/provider/${providerId}`);
      setStorageBoxes(response.data.storage_boxes || []);
      const mode = response.data.place_storage_mode;
      if (mode === 'split' || mode === 'combined' || mode === 'central') {
        setResolvedPlaceStorageMode(mode);
      }
    } catch (e) {
      console.error(e);
    }
  }, [providerId]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await apiService.getCategories();
      setCategoriesList(response.data.categories || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`/provider-items/provider/${providerId}`);
      setItems(response.data.provider_items || []);
    } catch (e) {
      console.error(e);
      showSnackbar('Erreur lors du chargement des lieux', 'error');
    } finally {
      setLoading(false);
    }
  }, [providerId]);

  useEffect(() => {
    if (placeStorageModeProp === 'split' || placeStorageModeProp === 'central' || placeStorageModeProp === 'combined') {
      setResolvedPlaceStorageMode(placeStorageModeProp);
    }
  }, [placeStorageModeProp]);

  useEffect(() => {
    void fetchItems();
    void fetchStorageBoxes();
    void fetchCategories();
  }, [fetchItems, fetchStorageBoxes, fetchCategories]);

  const handleOpenBoxDialog = (presetPlaceId?: number, box?: ProviderStorageBoxRow) => {
    if (box) {
      setEditingBox(box);
      setBoxForm({
        provider_item_id: box.provider_item_id ?? box.provider_place?.id ?? '',
        category_id: box.category_id ?? box.category?.id ?? '',
        name: box.name || '',
        description: box.description || '',
        sort_order: box.sort_order ?? 0,
        is_active: box.is_active !== false,
      });
    } else {
      setEditingBox(null);
      setBoxForm({
        provider_item_id: presetPlaceId ?? (items[0]?.id ?? ''),
        category_id: '',
        name: '',
        description: '',
        sort_order: storageBoxes.length,
        is_active: true,
      });
    }
    setBoxDialogOpen(true);
  };

  const handleCloseBoxDialog = () => {
    setBoxDialogOpen(false);
    setEditingBox(null);
  };

  const handleSubmitBox = async () => {
    if (!editingBox) {
      const hasCategory =
        boxForm.category_id !== '' &&
        Number.isFinite(Number(boxForm.category_id)) &&
        Number(boxForm.category_id) > 0;
      if (!hasCategory && !boxForm.name.trim()) {
        showSnackbar('Choisissez une catégorie ou saisissez un nom libre pour la boîte.', 'warning');
        return;
      }
      if (!usesCentralBoxes) {
        const placeId =
          typeof boxForm.provider_item_id === 'number' ? boxForm.provider_item_id : Number(boxForm.provider_item_id);
        if (!Number.isFinite(placeId) || placeId <= 0) {
          showSnackbar('Sélectionnez un lieu.', 'warning');
          return;
        }
      }
    }

    try {
      if (editingBox) {
        await apiService.put(`/provider-storage-boxes/${editingBox.id}`, {
          description: boxForm.description.trim() || null,
          sort_order: boxForm.sort_order,
          is_active: boxForm.is_active,
        });
        showSnackbar('Boîte mise à jour', 'success');
      } else {
        const hasCategory =
          boxForm.category_id !== '' &&
          Number.isFinite(Number(boxForm.category_id)) &&
          Number(boxForm.category_id) > 0;
        const cid = hasCategory ? Number(boxForm.category_id) : undefined;
        const payload: Record<string, unknown> = {
          description: boxForm.description.trim() || undefined,
          sort_order: boxForm.sort_order,
          is_active: boxForm.is_active,
        };
        if (cid) {
          payload.category_id = cid;
        } else {
          payload.name = boxForm.name.trim();
        }
        if (usesCentralBoxes) {
          await apiService.post(`/provider-storage-boxes/provider/${providerId}`, payload);
        } else {
          const placeId =
            typeof boxForm.provider_item_id === 'number' ? boxForm.provider_item_id : Number(boxForm.provider_item_id);
          await apiService.post(`/provider-storage-boxes/place/${placeId}`, payload);
        }
        showSnackbar('Boîte créée', 'success');
      }
      handleCloseBoxDialog();
      void fetchStorageBoxes();
    } catch (e) {
      console.error(e);
      showSnackbar('Erreur lors de la sauvegarde de la boîte', 'error');
    }
  };

  const handleDeleteBox = async (id: number) => {
    if (!window.confirm('Supprimer cette boîte ?')) return;
    try {
      await apiService.delete(`/provider-storage-boxes/${id}`);
      showSnackbar('Boîte supprimée', 'success');
      void fetchStorageBoxes();
    } catch (e) {
      console.error(e);
      showSnackbar('Erreur lors de la suppression', 'error');
    }
  };

  const handleOpenBulkDialog = () => {
    const activePlaceIds = items.filter((it) => it.is_active !== false).map((it) => it.id);
    setBulkForm({
      placeIds: activePlaceIds.length > 0 ? activePlaceIds : items.map((it) => it.id),
      categoryIds: categoriesList.map((c) => c.id),
      countPerCategory: 1,
    });
    setBulkDialogOpen(true);
  };

  const handleCloseBulkDialog = () => {
    if (bulkLoading) return;
    setBulkDialogOpen(false);
  };

  const toggleBulkPlace = (placeId: number) => {
    setBulkForm((prev) => ({
      ...prev,
      placeIds: prev.placeIds.includes(placeId)
        ? prev.placeIds.filter((id) => id !== placeId)
        : [...prev.placeIds, placeId],
    }));
  };

  const toggleBulkCategory = (categoryId: number) => {
    setBulkForm((prev) => ({
      ...prev,
      categoryIds: prev.categoryIds.includes(categoryId)
        ? prev.categoryIds.filter((id) => id !== categoryId)
        : [...prev.categoryIds, categoryId],
    }));
  };

  const selectAllBulkPlaces = () => {
    setBulkForm((prev) => ({ ...prev, placeIds: items.map((it) => it.id) }));
  };

  const selectNoBulkPlaces = () => {
    setBulkForm((prev) => ({ ...prev, placeIds: [] }));
  };

  const selectAllBulkCategories = () => {
    setBulkForm((prev) => ({ ...prev, categoryIds: categoriesList.map((c) => c.id) }));
  };

  const selectNoBulkCategories = () => {
    setBulkForm((prev) => ({ ...prev, categoryIds: [] }));
  };

  const handleBulkSubmit = async () => {
    if (bulkForm.placeIds.length === 0) {
      showSnackbar('Sélectionnez au moins un lieu.', 'warning');
      return;
    }
    if (bulkForm.categoryIds.length === 0) {
      showSnackbar('Sélectionnez au moins une catégorie.', 'warning');
      return;
    }
    const count = bulkCountPerCategory;
    setBulkLoading(true);
    try {
      const response = await apiService.post(`/provider-storage-boxes/provider/${providerId}/bulk-auto`, {
        place_ids: bulkForm.placeIds,
        category_ids: bulkForm.categoryIds,
        count_per_category: count,
      });
      const created = response.data?.created_count ?? 0;
      const apiErrors = (response.data?.errors || []) as string[];
      showSnackbar(response.data?.message || `${created} boîte(s) créée(s)`, 'success');
      if (apiErrors.length > 0) {
        showSnackbar(apiErrors.slice(0, 2).join(' · '), 'warning');
      }
      setBulkDialogOpen(false);
      void fetchStorageBoxes();
    } catch (e: unknown) {
      console.error(e);
      const err = e as { response?: { data?: { error?: string } } };
      showSnackbar(err.response?.data?.error || 'Erreur lors de la création automatique', 'error');
    } finally {
      setBulkLoading(false);
    }
  };

  const bulkCountPerCategory = Math.max(1, Math.min(50, bulkForm.countPerCategory || 1));
  const bulkPreviewCount = bulkForm.placeIds.length * bulkForm.categoryIds.length * bulkCountPerCategory;

  const handleOpenDialog = (item?: ProviderPlaceItem) => {
    if (item) {
      setEditing(item);
      setFormData({
        name: item.name,
        description: item.description || '',
        attributes: { ...item.attributes },
        sort_order: item.sort_order,
        is_active: item.is_active,
        color: resolveProviderPlaceColor(item.color, item.id),
        opening_hours: normalizeOpeningHours(readOpeningHoursFromPlace(item)),
      });
    } else {
      setEditing(null);
      const emptyAttrs: Record<string, unknown> = {};
      Object.keys(defaultAttributesTemplate).forEach((k) => {
        emptyAttrs[k] = '';
      });
      setFormData({
        name: '',
        description: '',
        attributes: emptyAttrs,
        sort_order: items.length,
        is_active: true,
        color: defaultProviderPlaceColorForIndex(items.length),
        opening_hours: emptyOpeningHours(),
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditing(null);
  };

  const handleSubmit = async () => {
    const validationError = validateOpeningHours(formData.opening_hours);
    if (validationError) {
      showSnackbar(validationError, 'warning');
      return;
    }
    const normalizedOpeningHours = normalizeOpeningHours(formData.opening_hours);
    const payload = {
      name: formData.name,
      description: formData.description,
      attributes: formData.attributes,
      sort_order: formData.sort_order,
      is_active: formData.is_active,
      color: formData.color,
      opening_hours: normalizedOpeningHours,
    };
    const sentHoursSignature = openingHoursSignature(normalizedOpeningHours);
    const sentHadHours = hasConfiguredOpeningHours(normalizedOpeningHours);
    try {
      const response = editing
        ? await apiService.put(`/provider-items/${editing.id}`, payload)
        : await apiService.post(`/provider-items/provider/${providerId}`, payload);
      const receivedHoursSignature = openingHoursSignature(response.data?.opening_hours);
      if (sentHadHours && receivedHoursSignature !== sentHoursSignature) {
        showSnackbar(
          'Le lieu a été enregistré mais les horaires ne semblent pas avoir été sauvegardés. Vérifiez que la migration API opening_hours est appliquée en production.',
          'warning',
        );
      } else {
        showSnackbar(editing ? 'Lieu mis à jour' : 'Lieu ajouté', 'success');
      }
      handleCloseDialog();
      void fetchItems();
      void fetchStorageBoxes();
    } catch (e: unknown) {
      console.error(e);
      const apiError =
        typeof e === 'object' &&
        e !== null &&
        'response' in e &&
        typeof (e as { response?: { data?: { error?: string } } }).response?.data?.error === 'string'
          ? (e as { response: { data: { error: string } } }).response.data.error
          : null;
      showSnackbar(apiError || 'Erreur lors de la sauvegarde', 'error');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Supprimer ce lieu ?')) return;
    try {
      await apiService.delete(`/provider-items/${id}`);
      showSnackbar('Lieu supprimé', 'success');
      void fetchItems();
      void fetchStorageBoxes();
    } catch (e) {
      console.error(e);
      showSnackbar('Erreur lors de la suppression', 'error');
    }
  };

  const handleToggleStatus = async (id: number) => {
    try {
      await apiService.patch(`/provider-items/${id}/toggle-status`);
      showSnackbar('Statut mis à jour', 'success');
      void fetchItems();
    } catch (e) {
      console.error(e);
      showSnackbar('Erreur', 'error');
    }
  };

  const handleAttributeChange = (key: string, value: unknown) => {
    setFormData((prev) => ({
      ...prev,
      attributes: { ...prev.attributes, [key]: value },
    }));
  };

  const renderAttributeField = (key: string, config: { type?: string; label?: string; options?: string[]; required?: boolean }) => {
    const value = formData.attributes[key];
    const label = config.label || key;
    switch (config.type) {
      case 'select':
        return (
          <FormControl fullWidth key={key}>
            <InputLabel>{label}</InputLabel>
            <Select
              value={(value as string) || ''}
              onChange={(e) => handleAttributeChange(key, e.target.value)}
              label={label}
            >
              {(config.options || []).map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case 'number':
        return (
          <TextField
            key={key}
            fullWidth
            label={label}
            type="number"
            value={value ?? ''}
            onChange={(e) => handleAttributeChange(key, e.target.value)}
          />
        );
      default:
        return (
          <TextField
            key={key}
            fullWidth
            label={label}
            value={(value as string) ?? ''}
            onChange={(e) => handleAttributeChange(key, e.target.value)}
            required={config.required}
          />
        );
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <PlaceIcon color="primary" />
        <Typography variant="h6">
          {isCentralMode ? 'Lieux de trouvaille / chambres' : 'Lieux / sous-espaces'}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {isCentralMode
          ? 'Créez les chambres ou lieux de trouvaille (lobby, restaurant…). Le stockage des objets reste centralisé dans les boîtes ci-dessous — pas de boîtes par chambre.'
          : isSplitMode
            ? 'Définissez les lieux de perte (chambres, halls, salles…). Le stockage des objets est centralisé — voir la section ci-dessous.'
            : 'Définissez les emplacements gérés par ce partenaire (ex. Chambre 101, Restaurant, Bar). Pour chaque lieu, vous pouvez ensuite créer des boîtes de stockage utilisées lors de la déclaration d\'objets trouvés.'}
      </Typography>

      <Box display="flex" justifyContent="flex-end" mb={1} gap={1} flexWrap="wrap">
        {!isCentralMode && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<AutoFixHighIcon />}
            onClick={handleOpenBulkDialog}
            disabled={(usesCentralBoxes ? false : items.length === 0) || categoriesList.length === 0}
          >
            Créer boîtes auto
          </Button>
        )}
        <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => handleOpenBoxDialog()}>
          Ajouter une boîte
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          {isCentralMode ? 'Ajouter une chambre / lieu' : 'Ajouter un lieu'}
        </Button>
      </Box>

      <>
          {loading && items.length === 0 ? (
            <Typography variant="body2">Chargement…</Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Couleur</TableCell>
                    <TableCell>Nom</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Horaires</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell>Ordre</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Typography variant="body2" color="text.secondary">
                          {isCentralMode
                            ? 'Aucune chambre / lieu de trouvaille — ajoutez-en avec le bouton ci-dessus.'
                            : 'Aucun lieu — ajoutez-en avec le bouton ci-dessus.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((item) => {
                      const placeColor = resolveProviderPlaceColor(item.color, item.id);
                      return (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: 1,
                              bgcolor: placeColor,
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                            title={placeColor}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight={600}>{item.display_name || item.name}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
                            {item.description || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 240 }}>
                            {formatOpeningHoursSummary(normalizeOpeningHours(readOpeningHoursFromPlace(item)))}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={item.is_active ? 'Actif' : 'Inactif'}
                            color={item.is_active ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{item.sort_order}</TableCell>
                        <TableCell align="right">
                          {!usesCentralBoxes && (
                            <IconButton
                              size="small"
                              color="secondary"
                              title="Boîte sur ce lieu"
                              onClick={() => handleOpenBoxDialog(item.id)}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          )}
                          <IconButton size="small" onClick={() => handleToggleStatus(item.id)}>
                            {item.is_active ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                          <IconButton size="small" color="primary" onClick={() => handleOpenDialog(item)}>
                            <EditIcon />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDelete(item.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
      </>

      <Divider sx={{ my: 3 }} />
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        {usesCentralBoxes ? 'Stockage central' : 'Boîtes de stockage'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {isCentralMode
          ? 'Boîtes de l’établissement. Catégorie optionnelle ; sinon nom libre.'
          : isSplitMode
            ? 'Boîtes communes à tout l’établissement (indépendantes des lieux de perte).'
            : 'Rattachées à un lieu ci-dessus ; visibles côté partenaire pour ranger les objets trouvés.'}
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {!isCentralMode && <TableCell>Lieu</TableCell>}
              <TableCell>Catégorie</TableCell>
              <TableCell>Boîte</TableCell>
              <TableCell>Ordre</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {storageBoxes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isCentralMode ? 5 : 6}>
                  <Typography variant="body2" color="text.secondary">
                    Aucune boîte — utilisez « Ajouter une boîte ».
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              storageBoxes.map((b) => (
                <TableRow key={b.id}>
                  {!isCentralMode && (
                    <TableCell>
                      {b.provider_place?.display_name || b.provider_place?.name || (usesCentralBoxes ? 'Stockage central' : '—')}
                    </TableCell>
                  )}
                  <TableCell>
                    <Typography variant="body2">{b.category?.name || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={600}>{b.name}</Typography>
                  </TableCell>
                  <TableCell>{b.sort_order}</TableCell>
                  <TableCell>
                    <Chip
                      label={b.is_active !== false ? 'Actif' : 'Inactif'}
                      color={b.is_active !== false ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" color="primary" onClick={() => handleOpenBoxDialog(undefined, b)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => void handleDeleteBox(b.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ResponsiveDialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Modifier le lieu' : 'Nouveau lieu'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Nom du lieu"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="Ex. Chambre 12, Bar, Restaurant…"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Ordre"
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value, 10) || 0 })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description (optionnel)"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Couleur d&apos;affichage (provider dashboard)
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1} mb={1.5}>
                {PROVIDER_PLACE_COLOR_PALETTE.map((paletteColor) => (
                  <Box
                    key={paletteColor}
                    role="button"
                    tabIndex={0}
                    onClick={() => setFormData((prev) => ({ ...prev, color: paletteColor }))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        setFormData((prev) => ({ ...prev, color: paletteColor }));
                      }
                    }}
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: 1,
                      bgcolor: paletteColor,
                      cursor: 'pointer',
                      border: '2px solid',
                      borderColor:
                        formData.color.toLowerCase() === paletteColor ? 'text.primary' : 'divider',
                    }}
                    title={paletteColor}
                  />
                ))}
              </Box>
              <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
                <TextField
                  type="color"
                  label="Nuancier"
                  value={formData.color}
                  onChange={(e) => {
                    const next = normalizeProviderPlaceColor(e.target.value);
                    if (next) setFormData((prev) => ({ ...prev, color: next }));
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 100 }}
                />
                <TextField
                  label="Code hex"
                  value={formData.color}
                  onChange={(e) => {
                    const next = normalizeProviderPlaceColor(e.target.value);
                    if (next) setFormData((prev) => ({ ...prev, color: next }));
                  }}
                  placeholder="#1976d2"
                  sx={{ flex: 1, minWidth: 140 }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      color: defaultProviderPlaceColorForIndex(prev.sort_order),
                    }))
                  }
                >
                  Couleur auto
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label="Actif"
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" gutterBottom>
                Horaires d&apos;ouverture
              </Typography>
              <OpeningHoursEditor
                value={formData.opening_hours}
                onChange={(opening_hours) => setFormData((prev) => ({ ...prev, opening_hours }))}
              />
            </Grid>
            {Object.keys(defaultAttributesTemplate).length > 0 && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Attributs supplémentaires (type de partenaire)
                </Typography>
                <Grid container spacing={2}>
                  {Object.entries(defaultAttributesTemplate).map(([key, config]) => (
                    <Grid item xs={12} sm={6} key={key}>
                      {renderAttributeField(key, config)}
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={!formData.name.trim()}>
            {editing ? 'Enregistrer' : 'Créer'}
          </Button>
        </DialogActions>
      </ResponsiveDialog>

      <ResponsiveDialog open={boxDialogOpen} onClose={handleCloseBoxDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingBox ? 'Modifier la boîte' : 'Nouvelle boîte'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {!editingBox && boxForm.category_id !== '' && creatableCategories.length === 0 && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  Aucune catégorie avec préfixe boîte (3 lettres). Définissez-le dans Gestion des catégories, ou créez une boîte sans catégorie.
                </Alert>
              </Grid>
            )}
            {!usesCentralBoxes && (
            <Grid item xs={12}>
              <FormControl fullWidth required disabled={!!editingBox}>
                <InputLabel>Lieu</InputLabel>
                <Select
                  label="Lieu"
                  value={boxForm.provider_item_id === '' ? '' : String(boxForm.provider_item_id)}
                  onChange={(e) => {
                    const v = e.target.value;
                    setBoxForm((prev) => ({
                      ...prev,
                      provider_item_id: v === '' ? '' : Number(v),
                    }));
                  }}
                >
                  {items.map((it) => (
                    <MenuItem key={it.id} value={String(it.id)}>
                      {it.display_name || it.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            )}
            {editingBox && (
              <Grid item xs={12}>
                <TextField fullWidth label="Nom de la boîte" value={editingBox.name} disabled />
              </Grid>
            )}
            {!editingBox && (
              <>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Catégorie (optionnel)</InputLabel>
                  <Select
                    label="Catégorie (optionnel)"
                    value={boxForm.category_id === '' ? '' : String(boxForm.category_id)}
                    onChange={(e) => {
                      const v = e.target.value;
                      setBoxForm((prev) => ({
                        ...prev,
                        category_id: v === '' ? '' : Number(v),
                        name: v === '' ? prev.name : '',
                      }));
                    }}
                  >
                    <MenuItem value="">
                      <em>Sans catégorie — nom libre</em>
                    </MenuItem>
                    {creatableCategories.map((c) => (
                      <MenuItem key={c.id} value={String(c.id)}>
                        {c.name} (
                        {String(c.storage_box_prefix).replace(/[^A-Za-z]/g, '').slice(0, 3).toUpperCase()}
                        -1, -2…)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Avec catégorie : nom automatique (préfixe + numéro). Sans : saisissez un nom libre.
                </Typography>
              </Grid>
              {boxForm.category_id === '' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="Nom de la boîte"
                    value={boxForm.name}
                    onChange={(e) => setBoxForm((prev) => ({ ...prev, name: e.target.value }))}
                    helperText="Ex. Boîte A, Coffre réception…"
                  />
                </Grid>
              )}
              </>
            )}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Ordre"
                type="number"
                value={boxForm.sort_order}
                onChange={(e) => setBoxForm({ ...boxForm, sort_order: parseInt(e.target.value, 10) || 0 })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description (optionnel)"
                value={boxForm.description}
                onChange={(e) => setBoxForm({ ...boxForm, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={boxForm.is_active}
                    onChange={(e) => setBoxForm({ ...boxForm, is_active: e.target.checked })}
                  />
                }
                label="Actif"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBoxDialog}>Annuler</Button>
          <Button
            onClick={() => void handleSubmitBox()}
            variant="contained"
            disabled={
              editingBox
                ? false
                : (!usesCentralBoxes && boxForm.provider_item_id === '') ||
                  (boxForm.category_id === ''
                    ? boxForm.name.trim() === ''
                    : creatableCategories.length === 0)
            }
          >
            {editingBox ? 'Enregistrer' : 'Créer'}
          </Button>
        </DialogActions>
      </ResponsiveDialog>

      <ResponsiveDialog open={bulkDialogOpen} onClose={handleCloseBulkDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Créer des boîtes automatiquement</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Génère des boîtes nommées <strong>PRÉFIXE-N</strong> (ex. CLE-1, TEL-2) pour chaque combinaison lieu ×
            catégorie. Les numéros continuent la série existante sur chaque lieu.
          </Typography>

          {categoriesList.length === 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Aucune catégorie d&apos;objet disponible. Créez-en dans Gestion des catégories.
            </Alert>
          )}

          <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography variant="subtitle2">Lieux</Typography>
            {items.length > 0 && (
              <Box>
                <Button size="small" onClick={selectAllBulkPlaces}>
                  Tout
                </Button>
                <Button size="small" onClick={selectNoBulkPlaces}>
                  Aucun
                </Button>
              </Box>
            )}
          </Box>
          {items.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Ajoutez d&apos;abord un lieu.
            </Typography>
          ) : (
            <FormGroup sx={{ mb: 2, maxHeight: 160, overflow: 'auto' }}>
              {items.map((it) => (
                <FormControlLabel
                  key={it.id}
                  control={
                    <Checkbox
                      checked={bulkForm.placeIds.includes(it.id)}
                      onChange={() => toggleBulkPlace(it.id)}
                    />
                  }
                  label={it.display_name || it.name}
                />
              ))}
            </FormGroup>
          )}

          <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography variant="subtitle2">Catégories d&apos;objet</Typography>
            {categoriesList.length > 0 && (
              <Box>
                <Button size="small" onClick={selectAllBulkCategories}>
                  Tout
                </Button>
                <Button size="small" onClick={selectNoBulkCategories}>
                  Aucun
                </Button>
              </Box>
            )}
          </Box>
          {categoriesList.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Aucune catégorie disponible.
            </Typography>
          ) : (
            <FormGroup sx={{ mb: 2, maxHeight: 220, overflow: 'auto' }}>
              {categoriesList.map((c) => {
                const prefix = formatCategoryBoxPrefix(c);
                return (
                  <FormControlLabel
                    key={c.id}
                    control={
                      <Checkbox
                        checked={bulkForm.categoryIds.includes(c.id)}
                        onChange={() => toggleBulkCategory(c.id)}
                      />
                    }
                    label={
                      <Box component="span" display="inline-flex" alignItems="center" gap={0.75} flexWrap="wrap">
                        <span>
                          {c.name} ({prefix}-1, {prefix}-2…)
                        </span>
                        {!categoryHasStoredPrefix(c) && (
                          <Chip size="small" label="préfixe auto" variant="outlined" color="info" />
                        )}
                      </Box>
                    }
                  />
                );
              })}
            </FormGroup>
          )}

          <TextField
            fullWidth
            type="number"
            label="Nombre de boîtes par catégorie et par lieu"
            inputProps={{ min: 1, max: 50 }}
            value={bulkForm.countPerCategory}
            onChange={(e) =>
              setBulkForm((prev) => ({
                ...prev,
                countPerCategory: Math.max(1, parseInt(e.target.value, 10) || 1),
              }))
            }
            helperText={`${bulkPreviewCount} boîte(s) seront créées au maximum.`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBulkDialog} disabled={bulkLoading}>
            Annuler
          </Button>
          <Button
            onClick={() => void handleBulkSubmit()}
            variant="contained"
            disabled={
              bulkLoading ||
              items.length === 0 ||
              categoriesList.length === 0 ||
              bulkForm.placeIds.length === 0 ||
              bulkForm.categoryIds.length === 0
            }
          >
            {bulkLoading ? 'Création…' : 'Générer les boîtes'}
          </Button>
        </DialogActions>
      </ResponsiveDialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProviderPlacesSection;
